import React,{useCallback,useEffect,useState} from 'react';
import {adminCompleteOrderIfAgreed,adminFetchOrderCompletionEvents} from '../../services/orderCompletionService';
import {supabase} from '../../supabase/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiCheckCircle,
  FiClock,
  FiRefreshCw,
  FiShield,
  FiAlertCircle
}=FiIcons;

const AdminOrderCompletionPanel=()=>{
  const [orders,setOrders]=useState([]);
  const [events,setEvents]=useState([]);
  const [loading,setLoading]=useState(true);
  const [busyOrderId,setBusyOrderId]=useState(null);
  const [error,setError]=useState('');

  const loadData=useCallback(async()=>{
    setLoading(true);
    setError('');

    try {
      const [{data:orderRows,error:orderError},eventRows]=await Promise.all([
        supabase
          .from('orders')
          .select(`
            id,
            status,
            delivery_method,
            buyer_id,
            seller_id,
            total_amount,
            created_at,
            buyer:users!orders_buyer_id_fkey(name,email),
            seller:sellers!orders_seller_id_fkey(business_name,user_id)
          `)
          .order('created_at',{ascending:false})
          .limit(200),
        adminFetchOrderCompletionEvents()
      ]);

      if (orderError) throw orderError;
      setOrders(orderRows || []);
      setEvents(eventRows);
    } catch (loadError) {
      setError(loadError.message || 'Unable to load completion records.');
    } finally {
      setLoading(false);
    }
  },[]);

  useEffect(()=>{
    loadData();
  },[loadData]);

  const completeOrder=async(order)=>{
    setBusyOrderId(order.id);
    setError('');

    try {
      await adminCompleteOrderIfAgreed({
        orderId: order.id,
        verificationMethod: 'mutual_party_agreement',
        securityMetadata: {
          interface: 'admin_order_completion_panel',
          reviewed_at: new Date().toISOString()
        }
      });
      await loadData();
    } catch (completionError) {
      setError(completionError.message || 'Unable to complete this order.');
    } finally {
      setBusyOrderId(null);
    }
  };

  if (loading) {
    return (
      <div className="rounded-[2rem] bg-white p-8 text-center shadow-sm">
        <SafeIcon icon={FiRefreshCw} className="mx-auto animate-spin text-2xl text-indigo-600" />
        <p className="mt-3 text-xs font-black uppercase tracking-widest text-slate-400">
          Loading completion records
        </p>
      </div>
    );
  }

  return (
    <section className="space-y-6">
      {error && (
        <div className="flex items-start rounded-2xl border border-red-100 bg-red-50 p-4 text-sm font-bold text-red-700">
          <SafeIcon icon={FiAlertCircle} className="mr-3 mt-0.5" />
          {error}
        </div>
      )}

      <div className="rounded-[2.5rem] border border-indigo-100 bg-indigo-50/50 p-8">
        <div className="flex flex-col justify-between gap-5 md:flex-row md:items-center">
          <div>
            <div className="flex items-center text-indigo-700">
              <SafeIcon icon={FiShield} className="mr-3 text-xl" />
              <h2 className="text-xl font-black">Order Completion Review</h2>
            </div>
            <p className="mt-2 max-w-2xl text-sm font-medium leading-relaxed text-indigo-900/70">
              Orders can only be completed here after both the buyer and seller have
              recorded their agreement.
            </p>
          </div>

          <button
            type="button"
            onClick={loadData}
            className="flex items-center rounded-xl bg-white px-4 py-3 text-[10px] font-black uppercase tracking-widest text-indigo-600 shadow-sm"
          >
            <SafeIcon icon={FiRefreshCw} className="mr-2" />
            Refresh
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-[2.5rem] border border-slate-100 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 text-left text-[10px] font-black uppercase tracking-widest text-slate-400">
                <th className="px-6 py-4">Order</th>
                <th className="px-6 py-4">Buyer</th>
                <th className="px-6 py-4">Seller</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {orders.map((order)=>{
                const event=events.find((item)=>item.order_id===order.id);
                const isCompleted=order.status==='completed' || Boolean(event);

                return (
                  <tr key={order.id}>
                    <td className="px-6 py-5">
                      <p className="font-black text-slate-900">
                        #{order.id.slice(0,8).toUpperCase()}
                      </p>
                      <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                        {order.delivery_method || 'pickup'}
                      </p>
                    </td>
                    <td className="px-6 py-5">
                      <p className="text-sm font-bold text-slate-900">
                        {order.buyer?.name || 'Unknown buyer'}
                      </p>
                      <p className="text-xs text-slate-400">
                        {order.buyer?.email || ''}
                      </p>
                    </td>
                    <td className="px-6 py-5 text-sm font-bold text-slate-900">
                      {order.seller?.business_name || 'Unknown seller'}
                    </td>
                    <td className="px-6 py-5">
                      <span className={`inline-flex items-center rounded-full px-3 py-1 text-[9px] font-black uppercase tracking-widest ${
                        isCompleted
                          ? 'bg-emerald-50 text-emerald-600'
                          : 'bg-amber-50 text-amber-600'
                      }`}>
                        <SafeIcon
                          icon={isCompleted ? FiCheckCircle : FiClock}
                          className="mr-2"
                        />
                        {isCompleted ? 'Completed' : order.status}
                      </span>
                    </td>
                    <td className="px-6 py-5 text-right">
                      {!isCompleted && (
                        <button
                          type="button"
                          onClick={()=>completeOrder(order)}
                          disabled={busyOrderId===order.id}
                          className="rounded-xl bg-indigo-600 px-4 py-3 text-[10px] font-black uppercase tracking-widest text-white disabled:opacity-50"
                        >
                          {busyOrderId===order.id
                            ? 'Completing...'
                            : 'Complete if Agreed'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="overflow-hidden rounded-[2.5rem] border border-slate-100 bg-white shadow-sm">
        <div className="border-b border-slate-100 p-8">
          <h2 className="flex items-center text-xl font-black text-slate-900">
            <SafeIcon icon={FiShield} className="mr-3 text-indigo-600" />
            Immutable Completion Events
          </h2>
          <p className="mt-2 text-sm font-medium text-slate-500">
            These records cannot be edited or deleted.
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 text-left text-[10px] font-black uppercase tracking-widest text-slate-400">
                <th className="px-6 py-4">Order</th>
                <th className="px-6 py-4">Timestamp</th>
                <th className="px-6 py-4">Seller</th>
                <th className="px-6 py-4">Verification</th>
                <th className="px-6 py-4">Token Fingerprint</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {events.map((event)=>(
                <tr key={event.event_id}>
                  <td className="px-6 py-5 font-black text-slate-900">
                    #{event.order_id.slice(0,8).toUpperCase()}
                  </td>
                  <td className="px-6 py-5 text-xs font-bold text-slate-500">
                    {new Date(event.completed_at).toLocaleString()}
                  </td>
                  <td className="px-6 py-5 text-sm font-bold text-slate-900">
                    {event.seller_name}
                  </td>
                  <td className="px-6 py-5">
                    <span className="rounded-full bg-indigo-50 px-3 py-1 text-[9px] font-black uppercase tracking-widest text-indigo-600">
                      {event.verification_method}
                    </span>
                  </td>
                  <td className="px-6 py-5 font-mono text-xs text-slate-500">
                    {event.token_fingerprint || 'Not supplied'}
                  </td>
                </tr>
              ))}
              {events.length===0 && (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-xs font-black uppercase tracking-widest text-slate-400">
                    No completion events recorded
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default AdminOrderCompletionPanel;